import {
  LitElement,
  html,
  css,
  classMap,
  t,
  utils,
  paths,
  unsafeHTML,
} from '@hh/global.js'
const { addToCart, reRenderShopifySections } = utils

class HHCartMessage extends LitElement {
  static properties = {
    active: {
      type: Boolean,
      reflect: true,
    },
    productTitle: {
      type: String,
    },
    productLink: {
      type: String,
    },
    variantId: {
      type: String,
    },
    quantity: {
      type: Number,
    },
    type: {
      type: String,
    },
    removalMessage: {
      type: String,
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('cart-message:undo', this.onUndo.bind(this))
    window.addEventListener('cart-message:added', this.onAdded.bind(this))
    window.addEventListener(
      'cart-message:added-all',
      this.onAddedAll.bind(this)
    )
    window.addEventListener(
      'cart-message:removed-not-available',
      this.onRemovedNotAvailable.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('cart-message:undo', this.onUndo)
    window.removeEventListener('cart-message:added', this.onAdded)
    window.removeEventListener('cart-message:added-all', this.onAddedAll)
  }

  onUndo(e) {
    const { detail } = e
    if (!detail) return

    this.active = true
    this.type = 'removed'

    const { title, link, variantId, quantity } = detail
    this.productTitle = title
    this.productLink = link
    this.variantId = variantId
    this.quantity = quantity
  }

  onAdded(e) {
    const { detail } = e
    if (!detail) return

    this.active = true
    this.type = 'added'

    const { title, link } = detail
    this.productTitle = title
    this.productLink = link
  }

  onAddedAll(e) {
    this.active = true
    this.type = 'added-all'
  }

  onRemovedNotAvailable(e) {
    const { detail } = e
    if (!detail) return

    this.active = true
    this.type = 'removed-not-available'

    const { title, message } = detail
    this.productTitle = title
    this.removalMessage = message
  }

  async reAddItem() {
    if (!this.variantId || !this.quantity) return

    this.setLoading(true)

    try {
      reRenderShopifySections(
        await addToCart(
          {
            id: this.variantId,
            quantity: this.quantity,
          },
          'mini-cart,inline-cart,header'
        )
      )

      window.dispatchEvent(
        new CustomEvent('cart-message:added', {
          bubbles: true,
          composed: true,
          detail: {
            title: this.productTitle,
            link: this.productLink,
          },
        })
      )
    } catch (e) {
      console.error({ error: e })
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(value) {
    this.inlineCart && (this.inlineCart.loading = value)
    this.miniCart && (this.miniCart.loading = value)
  }

  removeTemplate() {
    if (this.type !== 'removed') return

    return html`
      ${this.productLinkTemplate()}
      <span class="message">${t('cart.general.removed_form_cart')}</span>
      <button class="action" @click=${this.reAddItem}>
        ${t('cart.general.undo')}
      </button>
    `
  }

  addedTemplate() {
    if (this.type !== 'added') return

    return html`
      <span class="message">
        ${t('cart.general.added_to_cart_html').split('[[product_title]]')[0]}
        ${this.productLinkTemplate()}
        ${unsafeHTML(
          t('cart.general.added_to_cart_html').split('[[product_title]]')[1]
        )}
      </span>
    `
  }

  addedAllTemplate() {
    if (this.type !== 'added-all') return

    return html`
      <span class="message"> ${t('cart.general.added_all_to_cart')} </span>
    `
  }

  removedNotAvailableTemplate() {
    if (this.type !== 'removed-not-available') return

    return html`
      <span class="message">
        ${this.removalMessage || `${this.productTitle} is not available for purchase.`}
      </span>
    `
  }

  productLinkTemplate() {
    return html`
      <span class="product-title">
        "<a class="link" href=${this.productLink}>${this.productTitle}</a>"
      </span>
    `
  }

  get inlineCart() {
    return document.querySelector('hh-inline-cart')
  }

  get miniCart() {
    return document.querySelector('hh-mini-cart')
  }

  render() {
    const isError = this.type === 'removed-not-available'
    const errorIcon = `<svg
      class='icon icon-circle-error'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 17 16'
      fill='none'
      aria-hidden='true'
      focusable='false'
    >
      <path fill-rule="evenodd" clip-rule="evenodd" d="M8.5 15C12.366 15 15.5 11.866 15.5 8C15.5 4.13401 12.366 1 8.5 1C4.63401 1 1.5 4.13401 1.5 8C1.5 11.866 4.63401 15 8.5 15ZM8.5 16C12.9183 16 16.5 12.4183 16.5 8C16.5 3.58172 12.9183 0 8.5 0C4.08172 0 0.5 3.58172 0.5 8C0.5 12.4183 4.08172 16 8.5 16Z" fill="var(--hh-color-danger)"/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M8.5 9.5C8.22386 9.5 8 9.27614 8 9L8 5C8 4.72386 8.22386 4.5 8.5 4.5C8.77614 4.5 9 4.72386 9 5L9 9C9 9.27614 8.77614 9.5 8.5 9.5Z" fill="var(--hh-color-danger)"/>
      <path d="M8 11C8 10.7239 8.22386 10.5 8.5 10.5C8.77614 10.5 9 10.7239 9 11C9 11.2761 8.77614 11.5 8.5 11.5C8.22386 11.5 8 11.2761 8 11Z" fill="var(--hh-color-danger)"/>
    </svg>`
    
    return html`
      <div
        role="alert"
        aria-live="polite"
        class=${classMap({
          wrapper: true,
          'wrapper--error': isError,
          hide: !this.active,
        })}
      >
        <span class="icon ${isError ? 'icon--error' : ''}">${unsafeHTML(isError ? errorIcon : paths.circleCheckmark)}</span>
        <span>
          ${this.removeTemplate()} ${this.addedTemplate()}
          ${this.addedAllTemplate()} ${this.removedNotAvailableTemplate()}
        </span>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .wrapper {
      background-color: var(--hh-color-brand-tertiary);
      padding: var(--hh-spacing-xs) var(--hh-spacing-xxs);
      border-left: 0.3rem solid var(--hh-color-success);
      width: 100%;
      margin: var(--hh-margin, 0 0 var(--hh-spacing-md));
      display: flex;
      align-items: flex-start;
      font: var(--hh-body-bold-xs-font);
    }

    .wrapper--error {
      border-left-color: var(--hh-color-danger);
    }

    @media (min-width: 1024px) {
      .wrapper {
        padding: var(--hh-spacing-sm) var(--hh-spacing-md);
        align-items: center;
        font: var(--hh-body-bold-sm-font);
      }
    }

    .icon {
      color: var(--hh-color-success);
      width: var(--hh-spacing-md);
      height: var(--hh-spacing-md);
      margin: 0 var(--hh-spacing-xs) 0 0;
    }

    .icon--error {
      color: var(--hh-color-danger);
    }

    .link,
    .action {
      color: var(--hh-color-text);
      text-decoration: underline;
    }

    .link:hover,
    .action:hover {
      color: var(--hh-color-brand-primary);
    }

    .action {
      appearance: none;
      padding: 0;
      border: none;
      background: transparent;
      font: inherit;
      cursor: pointer;
    }

    .hide {
      display: none;
    }
  `
}

if (!customElements.get('hh-cart-message')) {
  customElements.define('hh-cart-message', HHCartMessage);
}